import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import { HiCheckCircle, HiXCircle, HiX } from "react-icons/hi";
import { Spinner } from "components/ui";
import { Tick } from "./Tick";

// Simple notification store
const notifications = [];
let notificationUpdater = null;

// Notification API
export const simpleNotify = {
  show: (data) => {
    const id = Date.now().toString();
    const notification = {
      id,
      status: data.status || "info",
      title: data.title || "Notification",
      message: data.message || "",
    };

    // Add to array
    notifications.push(notification);

    // Force update
    if (notificationUpdater) {
      notificationUpdater();
    }

    console.log("Added notification:", notification);
    return id;
  },

  update: (id, data) => {
    const index = notifications.findIndex((n) => n.id === id);
    if (index >= 0) {
      notifications[index] = {
        ...notifications[index],
        ...data,
      };

      if (notificationUpdater) {
        notificationUpdater();
      }
      console.log("Updated notification:", notifications[index]);
    }
  },

  remove: (id) => {
    const index = notifications.findIndex((n) => n.id === id);
    if (index >= 0) {
      notifications.splice(index, 1);
      if (notificationUpdater) {
        notificationUpdater();
      }
      console.log("Removed notification:", id);
    }
  },

  clear: () => {
    notifications.length = 0;
    if (notificationUpdater) {
      notificationUpdater();
    }
    console.log("Cleared all notifications");
  },

  test: () => {
    // Create test notification
    const id = simpleNotify.show({
      status: "processing",
      title: "Test Notification",
      message: "This is a test notification in processing state",
    });

    // Update after 2 seconds
    setTimeout(() => {
      simpleNotify.update(id, {
        status: "completed",
        title: "Test Completed",
        message: "Test notification completed successfully!",
      });

      // Auto removal will be handled by the component
    }, 2000);

    return id;
  },
};

// For testing from console
if (typeof window !== "undefined") {
  window.simpleNotify = simpleNotify;
}

// Individual notification card
const NotificationCard = ({ id, status, title, message, onClose }) => {
  // Style based on status
  const getStyle = () => {
    switch (status) {
      case "completed":
        return {
          bgColor: "bg-white",
          borderColor: "border-green-500",
          icon: <Tick className="text-xl text-green-500" />,
        };
      case "failed":
        return {
          bgColor: "bg-white",
          borderColor: "border-red-500",
          icon: <HiXCircle className="text-xl text-red-500" />,
        };
      default:
        return {
          bgColor: "bg-white",
          borderColor: "border-red-500",
          icon: <Spinner className="text-red-500" size={20} />,
        };
    }
  };

  const { bgColor, borderColor, icon } = getStyle();

  // Set up auto-close for completed/failed notifications
  useEffect(() => {
    if (["completed", "failed"].includes(status)) {
      const timer = setTimeout(() => {
        onClose(id);
      }, 5000); // Auto-remove after 5 seconds

      return () => clearTimeout(timer);
    }
  }, [id, status, onClose]);

  return (
    <div
      className={`${bgColor} border-l-4 ${borderColor} p-4 rounded shadow-md mb-2 flex items-start`}
    >
      <div className="mr-3 mt-0.5">{icon}</div>

      <div className="flex-1">
        {title && <div className="font-semibold">{title}</div>}
        {message && <div className="mt-1 text-sm text-gray-600">{message}</div>}
      </div>

      <button
        className="text-gray-400 hover:text-gray-600"
        onClick={() => onClose(id)}
      >
        <HiX />
      </button>
    </div>
  );
};

// Notification container
const NotificationContainer = () => {
  const [_, setUpdate] = useState(0);

  // Set up the updater function
  useEffect(() => {
    notificationUpdater = () => setUpdate((prev) => prev + 1);
    return () => {
      notificationUpdater = null;
    };
  }, []);

  if (notifications.length === 0) return null;

  return (
    <div className="fixed z-50 top-5 right-5 w-80">
      {notifications.map((notification) => (
        <NotificationCard
          key={notification.id}
          id={notification.id}
          status={notification.status}
          title={notification.title}
          message={notification.message}
          onClose={simpleNotify.remove}
        />
      ))}
    </div>
  );
};

// Component to include in App.js
const SimpleNotifyPortal = () => {
  // Using unmounted direct rendering instead of portal for maximum compatibility
  return <NotificationContainer />;
};

export default SimpleNotifyPortal;
